import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Printer,
  Scan,
  ShoppingCart,
  RotateCcw,
  ClipboardCheck,
  Truck,
  Package,
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronRight,
  Info,
} from 'lucide-react';

const Section = ({ id, title, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card
      id={id}
      className="border border-slate-200 print:border-slate-300 print:break-inside-avoid print:shadow-none"
      data-testid={`manual-section-${id}`}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left print:cursor-default hover:bg-slate-50 print:hover:bg-transparent"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center print:bg-white print:border print:border-slate-300">
            <Icon className="w-5 h-5 text-emerald-700" />
          </div>
          <h2 className="text-xl font-bold text-slate-900">{title}</h2>
        </div>
        <span className="text-slate-400 print:hidden">
          {open ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </span>
      </button>
      {(open) && (
        <CardContent className="border-t border-slate-100 pt-4 print:border-slate-300">
          {children}
        </CardContent>
      )}
    </Card>
  );
};

const Step = ({ n, children }) => (
  <li className="flex gap-3 print:break-inside-avoid">
    <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white text-sm font-bold flex items-center justify-center print:bg-white print:text-emerald-700 print:border print:border-emerald-700">
      {n}
    </span>
    <div className="flex-1 pt-0.5 text-sm leading-relaxed text-slate-700">{children}</div>
  </li>
);

const Tip = ({ children }) => (
  <div className="mt-3 bg-amber-50 border-l-4 border-amber-400 px-3 py-2 rounded text-xs text-amber-900 print:bg-white print:border-amber-700">
    <strong>Tip:</strong> {children}
  </div>
);

const StaffManual = () => {
  const handlePrint = () => window.print();

  const tocItems = [
    { id: 'sale', label: 'Processing a sale', icon: ShoppingCart },
    { id: 'refund', label: 'Processing a refund', icon: RotateCcw },
    { id: 'add-product', label: 'Adding a new product', icon: Package },
    { id: 'receive', label: 'Receiving stock (Smart PO)', icon: Truck },
    { id: 'negative', label: 'Clearing negative stock', icon: AlertTriangle },
    { id: 'stock-count', label: 'Doing a stock count', icon: ClipboardCheck },
  ];

  return (
    <div className="max-w-4xl mx-auto p-6 print:p-0 print:max-w-none" data-testid="staff-manual-page">
      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          aside, header, nav, .no-print { display: none !important; }
          .print\\:break-inside-avoid { break-inside: avoid; }
        }
      `}</style>

      <div className="flex items-start justify-between mb-6 print:mb-4 no-print">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">SupplyTrack Staff Manual</h1>
          <p className="text-slate-500 mt-1 text-sm">Quick guide for the things you do every day. Print this out and keep it by the till.</p>
        </div>
        <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="manual-print-btn">
          <Printer className="w-4 h-4 mr-2" /> Print this page
        </Button>
      </div>

      {/* Print header */}
      <div className="hidden print:block mb-6 border-b-2 border-slate-900 pb-3">
        <h1 className="text-2xl font-bold">SupplyTrack — Staff Quick Manual</h1>
        <p className="text-sm text-slate-600 mt-1">The Wellness Store · Orewa & Eastridge</p>
      </div>

      {/* Table of contents */}
      <Card className="mb-6 bg-slate-50 print:bg-white border border-slate-200 print:break-inside-avoid">
        <CardContent className="py-4">
          <p className="text-xs font-bold uppercase text-slate-500 mb-3">In this manual</p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {tocItems.map((t, i) => (
              <li key={t.id}>
                <a href={`#${t.id}`} className="flex items-center gap-2 text-slate-700 hover:text-emerald-700 print:text-slate-700">
                  <t.icon className="w-4 h-4 text-emerald-600" />
                  <span className="text-slate-400">{i + 1}.</span> {t.label}
                </a>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {/* 1. Processing a sale */}
        <Section id="sale" title="1. Processing a sale" icon={ShoppingCart}>
          <ol className="space-y-3">
            <Step n={1}>Go to <strong>POS</strong> (left sidebar). Check the store badge at the top — it should say Orewa or Eastridge correctly.</Step>
            <Step n={2}>Scan each product's barcode. It will appear in the cart on the right. Scan again to add another, or click the qty <strong>+ / −</strong> buttons.</Step>
            <Step n={3}>If a product won't scan, type its name in the search box and click it from the results.</Step>
            <Step n={4}>If the customer has a loyalty account, click <strong>Add Customer</strong> and search by name or phone. Their balance shows on screen.</Step>
            <Step n={5}>Click <strong>Charge</strong> (the big green button at the bottom of the cart) once everything is in.</Step>
            <Step n={6}>Choose <strong>Cash</strong>, <strong>EFTPOS</strong>, or <strong>Gift Card</strong>:
              <ul className="mt-1 list-disc list-inside ml-2 text-slate-600 text-xs space-y-0.5">
                <li><strong>EFTPOS</strong> — terminal lights up. Present card. Wait for "Approved" on screen.</li>
                <li><strong>Cash</strong> — enter the cash received, change is calculated.</li>
                <li><strong>Gift Card</strong> — scan or type the gift card number.</li>
              </ul>
            </Step>
            <Step n={7}>Receipt prints automatically. Done.</Step>
          </ol>
          <Tip>Need to reprint the last receipt? Click the small <strong>Last Receipt</strong> button at the top of the POS.</Tip>
        </Section>

        {/* 2. Refund */}
        <Section id="refund" title="2. Processing a refund" icon={RotateCcw}>
          <ol className="space-y-3">
            <Step n={1}>Go to <strong>POS</strong>.</Step>
            <Step n={2}>Click the <strong>Refund</strong> button (top right area of the POS).</Step>
            <Step n={3}>Search by date or customer name to find the original sale.</Step>
            <Step n={4}>Tick the products being returned, set the qty being refunded per line.</Step>
            <Step n={5}>Click <strong>Process Refund</strong>. Choose how to refund:
              <ul className="mt-1 list-disc list-inside ml-2 text-slate-600 text-xs space-y-0.5">
                <li><strong>EFTPOS</strong> — terminal will light up for refund. Use the customer's card.</li>
                <li><strong>Cash</strong> — give cash from the till.</li>
              </ul>
            </Step>
            <Step n={6}>Stock automatically goes back into inventory.</Step>
          </ol>
          <Tip>If the customer can't be found, ask for their receipt — you can use the receipt number to look it up directly.</Tip>
        </Section>

        {/* 3. Adding a product */}
        <Section id="add-product" title="3. Adding a new product" icon={Package}>
          <ol className="space-y-3">
            <Step n={1}>Go to <strong>Inventory → Products</strong> (left sidebar).</Step>
            <Step n={2}>Click <strong>+ Add Product</strong> (top right).</Step>
            <Step n={3}>Fill in:
              <ul className="mt-1 list-disc list-inside ml-2 text-slate-600 text-xs space-y-0.5">
                <li><strong>Name</strong> — exactly how it appears on the bottle</li>
                <li><strong>Barcode</strong> — scan it straight into the field</li>
                <li><strong>Supplier</strong> — pick from the list</li>
                <li><strong>Supplier Code</strong> — supplier's own SKU (from the invoice)</li>
                <li><strong>Buy price</strong> — wholesale cost ex GST</li>
                <li><strong>Sell price</strong> — retail price incl GST</li>
                <li><strong>Stock Orewa</strong> & <strong>Stock Eastridge</strong> — how many at each store</li>
              </ul>
            </Step>
            <Step n={4}>Check the margin badge at the bottom — must be at least <strong>42%</strong>. If not, raise the sell price.</Step>
            <Step n={5}>Click <strong>Save</strong>.</Step>
          </ol>
          <Tip>From the POS, you can also add a brand new product on the fly via <strong>Misc</strong> → <strong>Add Product</strong>. It's quicker for one-offs.</Tip>
        </Section>

        {/* 4. Receive Stock */}
        <Section id="receive" title="4. Receiving stock (Smart PO)" icon={Truck}>
          <ol className="space-y-3">
            <Step n={1}>Go to <strong>Smart PO</strong> (left sidebar, under Inventory).</Step>
            <Step n={2}>Find your supplier's <strong>Pending</strong> PO in the list, click to open it.</Step>
            <Step n={3}>Click <strong>Edit</strong>. The full line-item table opens.</Step>
            <Step n={4}>Go through each line:
              <ul className="mt-1 list-disc list-inside ml-2 text-slate-600 text-xs space-y-0.5">
                <li>Tick the <strong>qty received</strong> matches the supplier invoice. Adjust if short.</li>
                <li><strong>Buy Price</strong> — update if the invoice price differs from the PO.</li>
                <li><strong>Sell Price</strong> — type a new price right in the column if margin drops below 42%. Saves automatically.</li>
                <li>Add notes (e.g. <em>damaged</em>) by clicking the notes field.</li>
              </ul>
            </Step>
            <Step n={5}>The <strong>Margin badge</strong> per line shows the current %:
              <ul className="mt-1 list-disc list-inside ml-2 text-slate-600 text-xs space-y-0.5">
                <li><span className="text-emerald-700 font-semibold">Green (42%+)</span> = good</li>
                <li><span className="text-amber-700 font-semibold">Amber (30–42%)</span> = check with manager</li>
                <li><span className="text-red-700 font-semibold">Red (under 30%)</span> = fix before receiving</li>
              </ul>
            </Step>
            <Step n={6}>Once everything is correct, click <strong>Receive Stock</strong> at the bottom of the PO. Choose which store(s) to receive into.</Step>
            <Step n={7}>Confirm — stock goes into inventory immediately.</Step>
          </ol>
          <Tip>If freight or credit card surcharge is on the invoice, enter it in the <strong>Receive Setup</strong> step — it spreads the cost across all lines so margins stay accurate.</Tip>
        </Section>

        {/* 5. Negative stock */}
        <Section id="negative" title="5. Clearing negative stock" icon={AlertTriangle}>
          <p className="text-sm text-slate-600 mb-3">If a product shows a negative number (e.g. <strong>-3</strong>), it means the system thinks 3 units were sold that it never knew you bought. Usually it's because a PO was received physically but not booked in the app, or a wrong item got scanned.</p>
          <ol className="space-y-3">
            <Step n={1}>On the <strong>POS page</strong>, look for the red <strong>"Negative stock — click to fix"</strong> banner at the bottom.</Step>
            <Step n={2}>Click anywhere on the banner. A modal opens with all negative-stock products, grouped by supplier.</Step>
            <Step n={3}>For each item, count what's actually on the shelf and type that number in the <strong>"Actual on shelf"</strong> field. (Type <strong>0</strong> if nothing is left.)</Step>
            <Step n={4}>Press <strong>Enter</strong> or click <strong>Save</strong>. The item is corrected and disappears from the list.</Step>
            <Step n={5}>When you're done, click <strong>Done</strong> at the bottom.</Step>
          </ol>
          <Tip>The number you type <strong>replaces</strong> the current count — it's not added. So if it says -3 and you count 5 on the shelf, type 5 (not 8).</Tip>
          <Tip>If lots of items are negative under one supplier, it usually means their last delivery wasn't received in Smart PO. Check the printed invoice and run section 4 (Receive Stock) for that supplier.</Tip>
        </Section>

        {/* 6. Stock count */}
        <Section id="stock-count" title="6. Doing a full stock count" icon={ClipboardCheck}>
          <ol className="space-y-3">
            <Step n={1}>Go to <strong>Inventory → Stock Count</strong>.</Step>
            <Step n={2}>Pick the supplier (or shelf section) you're counting. Tap <strong>Start Count</strong>.</Step>
            <Step n={3}>For each product on the shelf, scan its barcode or search by name.</Step>
            <Step n={4}>Type how many you can physically see. Move to the next.</Step>
            <Step n={5}>If a product isn't on the shelf at all, leave its count as <strong>0</strong>.</Step>
            <Step n={6}>When all products for that supplier are counted, click <strong>Finalise</strong>. The system updates stock and logs the count.</Step>
            <Step n={7}>Repeat for the next supplier when you've got time.</Step>
          </ol>
          <Tip>For quick spot-fixes on just 1–2 items, use the <strong>Negative stock</strong> banner on the POS (section 5) instead of a full count.</Tip>
          <Tip>Try to do each supplier's count once every 2 months. The system shows an amber warning if any supplier hasn't been counted in 60+ days.</Tip>
        </Section>

        {/* 7. Short Dated Stock */}
        <Section id="short-dated" title="7. Short dated stock (selling near-expiry items)" icon={Calendar}>
          <p className="text-sm text-slate-600 mb-3">"Short dated" means a product is expiring soon and you want to clear it at a discount. The Short Dated system tracks these units <strong>separately</strong> from normal stock — so you can sell a few units cheap without touching the regular sell price.</p>

          <h3 className="text-sm font-bold text-slate-800 mt-4 mb-2">A. Adding a product to Short Dated</h3>
          <ol className="space-y-3">
            <Step n={1}>Go to <strong>Short Dated Stock</strong> in the side menu.</Step>
            <Step n={2}>Click <strong>+ Add</strong> (or scan the product's barcode).</Step>
            <Step n={3}>Enter the <strong>expiry date</strong>, <strong>quantity</strong> (how many short-dated units you have), and the <strong>discount %</strong> (default is 20%).</Step>
            <Step n={4}>Click <strong>Save</strong>. The discounted price is calculated automatically.</Step>
          </ol>
          <Tip>The short-dated entry is store-specific — Orewa and Eastridge are tracked separately. It's a "shadow" layer on top of normal stock, so your main inventory count is unchanged.</Tip>

          <h3 className="text-sm font-bold text-slate-800 mt-5 mb-2">B. What you see on the Short Dated page</h3>
          <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5">
            <li><strong>Expiry badge</strong>: red = ≤30 days, amber = ≤60 days, etc.</li>
            <li><strong>Discounted price</strong> shown next to the original.</li>
            <li><strong>Edit / Delete</strong> buttons on each row.</li>
            <li><strong>Monthly report</strong> card showing how much wholesale cost you're carrying in short-dated stock (loss tracking).</li>
          </ul>

          <h3 className="text-sm font-bold text-slate-800 mt-5 mb-2">C. Selling a short-dated item at POS</h3>
          <ol className="space-y-3">
            <Step n={1}>Scan or search the product at the till as normal.</Step>
            <Step n={2}>If that product has short-dated stock available, a popup appears: <em>"Sell as short-dated (X% OFF) or full price?"</em></Step>
            <Step n={3}>Pick <strong>Short Dated</strong> to add it at the discounted price (the cart row turns <span className="bg-yellow-100 px-1 rounded">yellow</span> and shows "(SHORT DATED)" on screen).</Step>
            <Step n={4}>Pick <strong>Full Price</strong> to sell it normally — no discount.</Step>
            <Step n={5}>Finish the sale as usual. Receipt prints the normal product name (no "SHORT DATED" tag for the customer).</Step>
          </ol>
          <Tip>You can have both lines in the same cart — e.g. 2 short-dated + 3 full-price of the same item.</Tip>
          <Tip>When the sale completes, the short-dated counter drops by however many you sold. Once it hits 0, the popup stops appearing for that product.</Tip>

          <h3 className="text-sm font-bold text-slate-800 mt-5 mb-2">D. When a product has expired</h3>
          <p className="text-sm text-slate-600">Expired items stay on the Short Dated page with a red <strong>EXPIRED</strong> badge — they do <strong>not</strong> auto-remove, so you can audit losses. Manually click <strong>Delete</strong> once the unit has been pulled from the shelf.</p>
        </Section>

        {/* Quick reference */}
        <Card className="bg-emerald-50 border-emerald-200 print:bg-white print:border-emerald-700 print:break-inside-avoid">
          <CardContent className="py-4">
            <p className="text-xs font-bold uppercase text-emerald-800 mb-2 flex items-center gap-2">
              <Info className="w-4 h-4" /> Quick reference
            </p>
            <ul className="text-sm text-slate-700 space-y-1.5">
              <li><strong>Receipt won't print?</strong> Check the QZ Tray icon is green in the system tray. If not, double-click it to restart.</li>
              <li><strong>EFTPOS won't light up?</strong> Make sure the right terminal is selected in the dropdown at the top of the POS. Try a different terminal if one was unpaired.</li>
              <li><strong>Customer can't find their loyalty?</strong> Search by phone (without spaces) — it's the most reliable.</li>
              <li><strong>Manager PIN</strong> (for margin override on Receive Stock): ask the manager directly.</li>
              <li><strong>Hard refresh</strong> (if something looks weird): <kbd className="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-xs">Ctrl</kbd> + <kbd className="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-xs">Shift</kbd> + <kbd className="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-xs">R</kbd></li>
            </ul>
          </CardContent>
        </Card>

        <div className="text-center text-xs text-slate-400 mt-6 print:mt-8 print:break-inside-avoid">
          <Scan className="w-4 h-4 inline mr-1" /> Keep this manual near the till — happy selling. 🌿
        </div>
      </div>
    </div>
  );
};

export default StaffManual;
